import { useEffect, useMemo, useRef, useState } from 'react';

type ModalStateAction =
  | {
      type: 'pending';
    }
  | {
      type: 'success';
    }
  | {
      type: 'error';
      title: string;
      description: string;
    };

export type ModalStateProps = {
  action?: ModalStateAction;
  forceClose?: boolean;
  isClosingDisabled?: boolean;
};

export const useModalState = (): {
  modalProps: ModalStateProps;
  trigger: {
    action: () => void;
    error: (props: { title: string; description: string }) => void;
    success: () => void;
  };
} => {
  const [modalProps, setModalProps] = useState<ModalStateProps>({});

  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  const trigger = useMemo(
    () => ({
      action: () => {
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);

        setModalProps({
          isClosingDisabled: true,
          action: {
            type: 'pending',
          },
        });
      },
      error: (error: { title: string; description: string }) => {
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);

        setModalProps({
          action: {
            type: 'error',
            ...error,
          },
        });
      },
      success: () => {
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);

        setModalProps({
          action: {
            type: 'success',
          },
          isClosingDisabled: false,
        });

        successTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            setModalProps({
              action: {
                type: 'success',
              },
              forceClose: true,
            });
          }
        }, 1500);
      },
    }),
    []
  );

  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    trigger,
    modalProps,
  };
};
